import { useState, useEffect, useCallback } from 'react';
import { X, StickyNote, Loader2, Plus, FileText, Plane, Navigation, Anchor, Users, Radar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { notesApi } from '@/services/notesApi';
import { fsopApi } from '@/services/api';
import { useToast } from '@/components/ui/use-toast';
import type { NoteListItem, NotePhase } from '@/types/notes.types';
import { useNavigate } from 'react-router-dom';

interface FlightLogNotesModalProps {
  flightLogId: number;
  onClose: () => void;
}

const PHASE_OPTIONS: { value: NotePhase; label: string; icon: React.ReactNode }[] = [
  { value: 'takeoff', label: '起飞', icon: <Navigation className="w-3.5 h-3.5" /> },
  { value: 'cruise', label: '航线', icon: <Plane className="w-3.5 h-3.5" /> },
  { value: 'landing', label: '落地', icon: <Anchor className="w-3.5 h-3.5" /> },
  { value: 'crew', label: '机组', icon: <Users className="w-3.5 h-3.5" /> },
  { value: 'general', label: '通用', icon: <Radar className="w-3.5 h-3.5" /> },
];

export function FlightLogNotesModal({ flightLogId, onClose }: FlightLogNotesModalProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [notes, setNotes] = useState<NoteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [phase, setPhase] = useState<NotePhase>('general');
  const [flightLog, setFlightLog] = useState<any>(null);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notesApi.getNotes({ flightLog: flightLogId });
      const results = res.data.results || [];
      setNotes(results);
    } catch {
      toast({ title: '获取笔记失败', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [flightLogId, toast]);

  useEffect(() => {
    fetchNotes();
    fsopApi
      .getFlightLog(flightLogId)
      .then((res) => setFlightLog(res.data.data))
      .catch(() => {
        // 静默失败
      });
  }, [fetchNotes, flightLogId]);

  const handleCreate = async () => {
    if (!title.trim()) {
      toast({ title: '请输入标题', variant: 'destructive' });
      return;
    }
    setCreating(true);

    const payload: any = {
      title: title.trim(),
      content: content.trim(),
      flightLog: flightLogId,
      phase,
    };

    // 根据 phase 预填关联
    if (flightLog) {
      if (phase === 'takeoff') {
        payload.departureAirportCode = flightLog.departure;
      } else if (phase === 'landing') {
        payload.arrivalAirportCode = flightLog.arrival;
      } else if (phase === 'cruise' || phase === 'general' || phase === 'crew') {
        payload.departureAirportCode = flightLog.departure;
        payload.arrivalAirportCode = flightLog.arrival;
      }
    }

    try {
      await notesApi.createNote(payload);
      toast({ title: '笔记已创建' });
      setTitle('');
      setContent('');
      setPhase('general');
      setShowForm(false);
      fetchNotes();
    } catch (err: any) {
      const msg = err?.response?.data?.message || '创建失败';
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const handleNoteClick = (noteId: number) => {
    navigate(`/notes/${noteId}`);
    onClose();
  };

  const handleGoToFullEdit = () => {
    navigate(`/notes/new?flightLog=${flightLogId}`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* overlay */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      {/* modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col">
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <StickyNote className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">飞行笔记</h3>
            <span className="text-sm text-gray-500">({notes.length})</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : notes.length === 0 && !showForm ? (
            <div className="text-center py-10">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-500 text-sm">暂无笔记</p>
              <p className="text-xs text-gray-400 mt-1">点击下方按钮添加第一条笔记</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <div
                  key={note.id}
                  onClick={() => handleNoteClick(note.id)}
                  className="border rounded-lg p-3 hover:border-blue-300 hover:bg-blue-50/30 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                      {note.phaseDisplay || note.phase}
                    </span>
                    <h4 className="font-medium text-gray-900 text-sm line-clamp-1">{note.title}</h4>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                    {note.content
                      ? note.content.replace(/[#*\-\[\]`]/g, '').slice(0, 80) +
                        (note.content.length > 80 ? '...' : '')
                      : '暂无内容'}
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                    <span>{new Date(note.updatedAt).toLocaleDateString('zh-CN')}</span>
                    {note.tags.length > 0 && (
                      <span className="px-1.5 py-0.5 bg-gray-100 rounded">
                        {note.tags[0]}
                        {note.tags.length > 1 ? ` +${note.tags.length - 1}` : ''}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showForm && (
            <div className="mt-4 border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">新建笔记</h4>
              <div className="space-y-3">
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="标题..."
                  className="text-sm"
                />
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="内容..."
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">关联维度</label>
                  <div className="flex flex-wrap gap-2">
                    {PHASE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setPhase(opt.value)}
                        className={cn(
                          'flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border transition-colors',
                          phase === opt.value
                            ? 'bg-blue-50 border-blue-300 text-blue-700'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        )}
                      >
                        {opt.icon}
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button size="sm" onClick={handleCreate} disabled={creating} className="gap-1">
                    {creating ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Plus className="w-3.5 h-3.5" />
                    )}
                    创建
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setTitle('');
                      setContent('');
                      setPhase('general');
                    }}
                  >
                    取消
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* footer */}
        {!showForm && (
          <div className="px-5 py-3 border-t space-y-2">
            <Button
              size="sm"
              onClick={handleGoToFullEdit}
              className="w-full gap-1"
            >
              <Plus className="w-4 h-4" />
              添加笔记
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function cn(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}
